import { supabase } from "./supabase.js";
import type { Achievement } from "./types.js";

interface AchievementRow {
  event_id: string;
  events: {
    name: string;
    tournaments: { id: string; name: string; starts_on: string };
  };
}

export function toAchievement(row: AchievementRow): Achievement {
  return {
    eventId: row.event_id,
    eventName: row.events.name,
    tournamentId: row.events.tournaments.id,
    tournamentName: row.events.tournaments.name,
    tournamentStartsOn: row.events.tournaments.starts_on,
  };
}

/** Later tournament first; within one, by event name so the order is the same every time. */
export function newestFirst(a: Achievement, b: Achievement): number {
  return (
    b.tournamentStartsOn.localeCompare(a.tournamentStartsOn) ||
    a.tournamentName.localeCompare(b.tournamentName) ||
    a.eventName.localeCompare(b.eventName)
  );
}

/**
 * The events a player was MVP of, one entry each.
 *
 * `event_mvps` already follows who can see the tournament, so an MVP of a private tournament
 * simply is not returned to somebody who cannot see it — nothing here filters. It reaches the
 * tournament through its event along the one foreign key each way, so there is no second path
 * for PostgREST to refuse as ambiguous, and no id list is fed back into a URL.
 */
export async function fetchAchievements(memberId: string): Promise<Achievement[]> {
  if (!supabase) return [];

  const { data, error } = await supabase
    .from("event_mvps")
    .select("event_id, events!inner (name, tournaments!inner (id, name, starts_on))")
    .eq("member_id", memberId);

  if (error) throw new Error(error.message);
  return (data as unknown as AchievementRow[]).map(toAchievement).sort(newestFirst);
}
